import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { useNavigate } from 'react-router-dom';
import { Elabels } from 'pages/CaseManagementPage/CasesPage/helpers';
import { Box, Grid, Typography } from '@mui/material';
import { INoteFormValue } from 'models/cases';
import { Resolver, useForm } from 'react-hook-form';
import { useFormSchema } from './schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';

interface IFormNote {
  isEdit: boolean;
  initialValues: INoteFormValue | null;
  onSubmit: (data: INoteFormValue) => void;
  backLink: string;
}

const initialFormValues = {
  name: '',
  description: '',
};

const FormNote = ({ isEdit, initialValues, onSubmit, backLink }: IFormNote) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const { schema } = useFormSchema();

  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    setError,
    reset,
    formState: { errors },
  } = useForm<INoteFormValue>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<INoteFormValue>,
  });

  useEffect(() => {
    if (isEdit && initialValues) {
      reset(initialValues);
    }
  }, [isEdit, initialValues]);

  const onSubmitForm = (data: INoteFormValue) => {
    setSubmitLoading(true);
    onSubmit(data);
    setSubmitLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Grid container size={{ xs: 12 }}>
        <Grid size={{ xs: 12 }}>
          <FormInput
            name="name"
            label={t(Elabels.notesName)}
            setValue={setValue}
            control={control}
            errors={errors}
            required
            id="form_note_name-input"
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ minHeight: '50vh' }}>
          <FormWyswigEditor
            name="description"
            label={t(Elabels.description)}
            setValue={setValue}
            control={control}
            errors={errors}
            setError={setError}
            clearErrors={clearErrors}
            charsLimit={10000}
            required
            fullContainer
            showCounter
          />
        </Grid>
      </Grid>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: '20px',
          maxWidth: 780,
          mt: '48px',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Button
            type="submit"
            fullWidth
            label={t('general.save.button')}
            id="save-button"
            variant={EButtonVariants.contained}
            isLoading={isSubmitLoading}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Button
            fullWidth
            label={t('general.cancel.button')}
            id="cancel-button"
            variant={EButtonVariants.outlined}
            onClick={() => navigate(backLink)}
          />
        </Box>
      </Box>
      <Grid sx={{ mt: 1 }}>
        <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
      </Grid>
    </form>
  );
};

export default FormNote;
